import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { apiRequest, buildUrl } from "../../lib/request"
import { normalizeApiAssetList, normalizeApiAsset } from "../../components/MediaLibrary/mediaLibraryMediaUtils"

/**
 * Media Library API slice.
 *
 * GET   /media-library/clients/:clientId/folders
 * POST  /media-library/clients/:clientId/folders  { name } or { name, parentId }
 * PATCH  /media-library/clients/:clientId/folders/:folderId  { name }
 * DELETE /media-library/clients/:clientId/folders/:folderId
 * GET    /media-library/clients/:clientId/stats
 * GET    /media-library/clients/:clientId/folders/:folderId/download
 * GET    /media-library/clients/:clientId/assets/:id/download
 * POST   /media-library/clients/:clientId/assets  multipart files
 * POST   /media-library/clients/:clientId/folders/:folderId/assets  multipart files
 * GET    /media-library/clients/:clientId/assets?type=all&sort=newest
 * GET    /media-library/clients/:clientId/folders/:folderId/assets?type=all&sort=newest
 * POST   /media-library/clients/:clientId/assets/move  { ids, targetFolderId }
 * PATCH  /media-library/clients/:clientId/assets/:id  { name }
 * DELETE /media-library/clients/:clientId/assets?ids=1&ids=2
 */

function normalizeParentIdFromApi(parentId) {
  if (parentId == null || parentId === 0 || parentId === "0") return null
  return String(parentId)
}

function mergeFoldersForParentLevel(existingFolders, incomingFolders, parentId) {
  const normalizedIncoming = normalizeFoldersForParentLevel(incomingFolders, parentId)
  const existingById = new Map((existingFolders ?? []).map((folder) => [String(folder.id), folder]))
  const incomingIds = new Set(normalizedIncoming.map((folder) => String(folder.id)))
  const retained = (existingFolders ?? []).filter((folder) => !incomingIds.has(String(folder.id)))
  const mergedIncoming = normalizedIncoming.map((folder) => {
    const previous = existingById.get(String(folder.id))
    return {
      ...previous,
      ...folder,
      sizeBytes: keepFolderSizeBytes(previous, folder),
    }
  })

  return [...retained, ...mergedIncoming]
}

function readFolderSizeBytes(folder) {
  const raw = folder?.size ?? folder?.sizeBytes
  if (raw == null || raw === "") return null
  const size = Number(raw)
  return Number.isFinite(size) && size >= 0 ? size : null
}

function keepFolderSizeBytes(previous, next) {
  const incoming = Number(next?.sizeBytes)
  if (next?.sizeBytes != null && Number.isFinite(incoming) && incoming >= 0) return incoming
  const existing = Number(previous?.sizeBytes)
  if (previous?.sizeBytes != null && Number.isFinite(existing) && existing >= 0) return existing
  return 0
}

function normalizeApiFolder(apiData, fallbackParentId = null) {
  const folder = apiData?.folder ?? apiData?.data ?? apiData ?? {}
  const id = folder.id ?? folder.folderId ?? folder.folder_id

  return {
    id: id != null ? String(id) : null,
    name: String(folder.name ?? "").trim(),
    parentId: normalizeParentIdFromApi(
      folder.parentId ?? folder.parentFolderId ?? fallbackParentId,
    ),
    sizeBytes: readFolderSizeBytes(folder),
    createdAt:
      folder.insertedDatetime ??
      folder.createdAt ??
      folder.created_at ??
      new Date().toISOString(),
  }
}

function normalizeApiFolderList(response, requestedParentId = null) {
  const list = Array.isArray(response)
    ? response
    : Array.isArray(response?.folders)
      ? response.folders
      : Array.isArray(response?.data)
        ? response.data
        : []

  const fallbackParentId =
    requestedParentId != null && requestedParentId !== "" ? String(requestedParentId) : null

  return list
    .map((item) => normalizeApiFolder(item, fallbackParentId))
    .filter((folder) => folder.id && folder.name)
}

function folderParentKey(parentId) {
  return parentId == null || parentId === "" || parentId === 0 || parentId === "0"
    ? "root"
    : String(parentId)
}

function normalizeFoldersForParentLevel(folders, parentId) {
  const normalizedParentId =
    parentId == null || parentId === "" || parentId === 0 || parentId === "0"
      ? null
      : String(parentId)

  return (folders ?? []).map((folder) => ({
    ...folder,
    parentId: normalizedParentId ?? normalizeParentIdFromApi(folder.parentId),
  }))
}

function formatStoragePercent(value) {
  const num = Math.min(100, Math.max(0, Number(value) || 0))
  if (!Number.isFinite(num)) return 0
  return parseFloat(num.toFixed(1))
}

function assetMatchesId(asset, assetId) {
  if (!asset || assetId == null) return false
  const target = String(assetId)
  return [asset.id, asset.id_media, asset.media_asset_id, asset.mediaAssets?.[0]?.id]
    .filter((value) => value != null && value !== "")
    .some((value) => String(value) === target)
}

function applyAssetRenameToList(assets, assetId, name, normalizedAsset) {
  if (!Array.isArray(assets)) return assets
  let changed = false
  const next = assets.map((asset) => {
    if (!assetMatchesId(asset, assetId)) return asset
    changed = true
    if (normalizedAsset) return { ...asset, ...normalizedAsset }
    return { ...asset, asset_filename: name, name }
  })
  return changed ? next : assets
}

function normalizeApiStats(data) {
  const storage = data?.storage ?? {}
  const limitBytes = Number(storage.limitBytes) || 0
  const usedBytes = Number(storage.usedBytes) || 0
  const imageBytes = Number(storage.imageBytes) || 0
  const videoBytes = Number(storage.videoBytes) || 0
  const usedPercentRaw =
    storage.usedPercent != null && storage.usedPercent !== ""
      ? Number(storage.usedPercent)
      : limitBytes > 0
        ? (usedBytes / limitBytes) * 100
        : 0
  const usedPercent = formatStoragePercent(
    Number.isFinite(usedPercentRaw) ? usedPercentRaw : 0,
  )

  return {
    totalFiles: Number(data?.totalFiles) || 0,
    totalFolders: Number(data?.totalFolders) || 0,
    uploadedThisMonth: Number(data?.uploadedThisMonth) || 0,
    storage: {
      limitBytes,
      usedBytes,
      availableBytes:
        Number(storage.availableBytes) || Math.max(0, limitBytes - usedBytes),
      imageBytes,
      videoBytes,
      otherBytes: Number(storage.otherBytes) || 0,
      usedPercent,
      imagePercent: formatStoragePercent(
        limitBytes > 0 ? (imageBytes / limitBytes) * 100 : 0,
      ),
      videoPercent: formatStoragePercent(
        limitBytes > 0 ? (videoBytes / limitBytes) * 100 : 0,
      ),
    },
  }
}

export const fetchMediaLibraryStats = createAsyncThunk(
  "mediaLibrary/fetchStats",
  async ({ clientId }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }

      const response = await apiRequest(`/media-library/clients/${clientId}/stats`)
      const stats = normalizeApiStats(response?.data ?? response)

      return { clientId, stats }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load library stats.")
    }
  },
)

function getFilenameFromDisposition(disposition, fallback) {
  if (!disposition) return fallback

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1])
  }

  const quotedMatch = disposition.match(/filename="([^"]+)"/i)
  if (quotedMatch?.[1]) {
    return quotedMatch[1]
  }

  const plainMatch = disposition.match(/filename=([^;]+)/i)
  if (plainMatch?.[1]) {
    return plainMatch[1].trim()
  }

  return fallback
}

function triggerBlobDownload(blob, filename) {
  const blobUrl = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = blobUrl
  link.setAttribute("download", filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(blobUrl)
}

export const downloadMediaLibraryFolder = createAsyncThunk(
  "mediaLibrary/downloadFolder",
  async ({ clientId, folderId, folderName }, { rejectWithValue, getState }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }
      if (folderId == null || folderId === "") {
        throw new Error("Folder is required.")
      }

      const token = getState().auth?.token || localStorage.getItem("token")
      const url = buildUrl(`/media-library/clients/${clientId}/folders/${folderId}/download`)

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "*/*",
        },
      })

      if (!response.ok) {
        let errorMessage = `Download failed (${response.status})`
        try {
          const errorData = await response.json()
          errorMessage = errorData?.message || errorData?.error || errorMessage
        } catch {
          const errorText = await response.text().catch(() => "")
          if (errorText) errorMessage = errorText
        }
        throw new Error(errorMessage)
      }

      const disposition = response.headers.get("content-disposition")
      const safeFolderName = String(folderName || "media-library-folder")
        .trim()
        .replace(/[\\/:*?"<>|]/g, "_")
      const fallbackName = `${safeFolderName || "media-library-folder"}.zip`
      const filename = getFilenameFromDisposition(disposition, fallbackName)

      const blob = await response.blob()
      triggerBlobDownload(blob, filename)

      return { clientId, folderId: String(folderId), filename }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to download folder.")
    }
  },
)

export const downloadMediaLibraryAsset = createAsyncThunk(
  "mediaLibrary/downloadAsset",
  async ({ clientId, assetId, filename }, { rejectWithValue, getState }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }

      const id = Number(assetId)
      if (!Number.isFinite(id) || id <= 0) {
        throw new Error("Asset is required.")
      }

      const token = getState().auth?.token || localStorage.getItem("token")
      const url = buildUrl(`/media-library/clients/${clientId}/assets/${id}/download`)

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "*/*",
        },
      })

      if (!response.ok) {
        let errorMessage = `Download failed (${response.status})`
        try {
          const errorData = await response.json()
          errorMessage = errorData?.message || errorData?.error || errorMessage
        } catch {
          const errorText = await response.text().catch(() => "")
          if (errorText) errorMessage = errorText
        }
        throw new Error(errorMessage)
      }

      const disposition = response.headers.get("content-disposition")
      const safeName = String(filename || `asset-${id}`)
        .trim()
        .replace(/[\\/:*?"<>|]/g, "_")
      const downloadFilename = getFilenameFromDisposition(disposition, safeName || `asset-${id}`)

      const blob = await response.blob()
      triggerBlobDownload(blob, downloadFilename)

      return { clientId, assetId: String(id), filename: downloadFilename }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to download file.")
    }
  },
)

function mapUiSortToApiSort(sort) {
  if (!sort) return "newest"
  if (sort === "newest-first") return "newest"
  if (sort === "oldest-first") return "oldest"
  return sort
}

export const fetchMediaLibraryFolderAssets = createAsyncThunk(
  "mediaLibrary/fetchFolderAssets",
  async ({ clientId, folderId, type = "all", sort = "newest", search }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }
      if (folderId == null || folderId === "") {
        throw new Error("Folder is required.")
      }

      const params = new URLSearchParams()
      if (type) params.set("type", type)
      const apiSort = mapUiSortToApiSort(sort)
      if (apiSort) params.set("sort", apiSort)
      if (search) {
        params.set("search", search)
      }

      const query = params.toString()
      const url = `/media-library/clients/${clientId}/folders/${folderId}/assets${query ? `?${query}` : ""}`
      const response = await apiRequest(url)
      const normalizedFolderId = String(folderId)
      const assets = normalizeApiAssetList(response).map((asset) => ({
        ...asset,
        _apiFolderId: asset._apiFolderId ?? normalizedFolderId,
      }))

      return { clientId, folderId: normalizedFolderId, assets }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load folder assets.")
    }
  },
)

export const moveMediaLibraryAssets = createAsyncThunk(
  "mediaLibrary/moveAssets",
  async ({ clientId, assetIds, targetFolderId = null }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }

      const ids = (Array.isArray(assetIds) ? assetIds : [assetIds])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)

      if (!ids.length) {
        throw new Error("No valid assets to move.")
      }

      const numericTargetFolderId =
        targetFolderId != null && targetFolderId !== "" ? Number(targetFolderId) : 0

      const response = await apiRequest(`/media-library/clients/${clientId}/assets/move`, {
        method: "POST",
        body: JSON.stringify({
          ids,
          targetFolderId: Number.isFinite(numericTargetFolderId) ? numericTargetFolderId : 0,
        }),
      })

      const assets = normalizeApiAssetList(response)
      const resolvedTargetFolderId =
        Number.isFinite(numericTargetFolderId) && numericTargetFolderId > 0
          ? String(numericTargetFolderId)
          : null

      return {
        clientId,
        assetIds: ids.map(String),
        targetFolderId: resolvedTargetFolderId,
        assets,
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to move assets.")
    }
  },
)

export const fetchMediaLibraryAssets = createAsyncThunk(
  "mediaLibrary/fetchAssets",
  async ({ clientId, type = "all", sort = "newest", folderId, search }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }

      const params = new URLSearchParams()
      if (type) params.set("type", type)
      const apiSort = mapUiSortToApiSort(sort)
      if (apiSort) params.set("sort", apiSort)
      if (folderId != null && folderId !== "") {
        params.set("folderId", String(folderId))
      }
      if (search) {
        params.set("search", search)
      }

      const query = params.toString()
      const url = `/media-library/clients/${clientId}/assets${query ? `?${query}` : ""}`
      const response = await apiRequest(url)
      const assets = normalizeApiAssetList(response)

      return { clientId, assets }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load media assets.")
    }
  },
)

export const fetchMediaLibraryFolders = createAsyncThunk(
  "mediaLibrary/fetchFolders",
  async ({ clientId, parentId, search, silent = false }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }

      const params = new URLSearchParams()
      if (parentId != null && parentId !== "") {
        params.set("parentId", String(parentId))
      }
      if (search) {
        params.set("search", search)
      }

      const query = params.toString()
      const url = `/media-library/clients/${clientId}/folders${query ? `?${query}` : ""}`
      const response = await apiRequest(url)
      const requestedParentId = parentId != null && parentId !== "" ? String(parentId) : null
      const folders = normalizeApiFolderList(response, requestedParentId)

      return {
        clientId,
        folders,
        parentId: requestedParentId,
        silent: Boolean(silent),
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to load folders.")
    }
  },
)

export const createMediaLibraryFolder = createAsyncThunk(
  "mediaLibrary/createFolder",
  async ({ clientId, name, parentId = null }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }

      const trimmed = String(name || "").trim()
      if (!trimmed) {
        throw new Error("Folder name is required.")
      }

      const payload = { name: trimmed }
      if (parentId != null && parentId !== "") {
        const numericParentId = Number(parentId)
        if (!Number.isFinite(numericParentId)) {
          throw new Error("Invalid parent folder.")
        }
        payload.parentId = numericParentId
      }

      const response = await apiRequest(`/media-library/clients/${clientId}/folders`, {
        method: "POST",
        body: JSON.stringify(payload),
      })

      const folder = normalizeApiFolder(response, parentId)
      if (!folder.id || !folder.name) {
        throw new Error("Invalid folder response from server.")
      }

      return { clientId, folder }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to create folder.")
    }
  },
)

export const updateMediaLibraryFolder = createAsyncThunk(
  "mediaLibrary/updateFolder",
  async ({ clientId, folderId, name }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }
      if (folderId == null || folderId === "") {
        throw new Error("Folder is required.")
      }

      const trimmed = String(name || "").trim()
      if (!trimmed) {
        throw new Error("Folder name is required.")
      }

      const response = await apiRequest(
        `/media-library/clients/${clientId}/folders/${folderId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ name: trimmed }),
        },
      )

      const folder = normalizeApiFolder(response)
      if (!folder.id || !folder.name) {
        throw new Error("Invalid folder response from server.")
      }

      return { clientId, folder }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to rename folder.")
    }
  },
)

export const deleteMediaLibraryFolder = createAsyncThunk(
  "mediaLibrary/deleteFolder",
  async ({ clientId, folderId, descendantFolderIds }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }
      if (folderId == null || folderId === "") {
        throw new Error("Folder is required.")
      }

      await apiRequest(`/media-library/clients/${clientId}/folders/${folderId}`, {
        method: "DELETE",
      })

      return {
        clientId,
        folderId: String(folderId),
        descendantFolderIds: (descendantFolderIds ?? [folderId]).map(String),
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to delete folder.")
    }
  },
)

export const uploadMediaLibraryAssets = createAsyncThunk(
  "mediaLibrary/uploadAssets",
  async ({ clientId, files, folderId = null }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }
      if (!files?.length) {
        throw new Error("No files selected.")
      }

      const formData = new FormData()
      files.forEach((file) => {
        formData.append("files", file)
      })

      const numericFolderId =
        folderId != null && folderId !== "" ? Number(folderId) : null
      const uploadUrl =
        Number.isFinite(numericFolderId) && numericFolderId > 0
          ? `/media-library/clients/${clientId}/folders/${numericFolderId}/assets`
          : `/media-library/clients/${clientId}/assets`

      const response = await apiRequest(uploadUrl, {
        method: "POST",
        body: formData,
      })

      const assets = normalizeApiAssetList(response)

      if (!assets.length) {
        throw new Error("Upload completed but no assets were returned.")
      }

      const resolvedFolderId =
        Number.isFinite(numericFolderId) && numericFolderId > 0
          ? String(numericFolderId)
          : null

      return { clientId, assets, folderId: resolvedFolderId }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to upload files.")
    }
  },
)

export const updateMediaLibraryAsset = createAsyncThunk(
  "mediaLibrary/updateAsset",
  async ({ clientId, assetId, name }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }

      const id = Number(assetId)
      if (!Number.isFinite(id) || id <= 0) {
        throw new Error("Asset is required.")
      }

      const trimmed = String(name || "").trim()
      if (!trimmed) {
        throw new Error("File name is required.")
      }

      const response = await apiRequest(`/media-library/clients/${clientId}/assets/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: trimmed }),
      })

      const asset = normalizeApiAsset(response?.asset ?? response?.data ?? response)
      const resolvedName = asset?.asset_filename || response?.name || trimmed

      return {
        clientId,
        assetId: String(id),
        name: resolvedName,
        asset,
      }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to rename file.")
    }
  },
)

export const deleteMediaLibraryAssets = createAsyncThunk(
  "mediaLibrary/deleteAssets",
  async ({ clientId, assetIds }, { rejectWithValue }) => {
    try {
      if (!clientId) {
        throw new Error("Please select a client first.")
      }

      const ids = (Array.isArray(assetIds) ? assetIds : [assetIds])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)

      if (!ids.length) {
        throw new Error("No valid assets to delete.")
      }

      const query = ids.map((id) => `ids=${encodeURIComponent(id)}`).join("&")
      await apiRequest(`/media-library/clients/${clientId}/assets?${query}`, {
        method: "DELETE",
      })

      return { clientId, assetIds: ids.map(String) }
    } catch (error) {
      return rejectWithValue(error?.message || "Failed to delete assets.")
    }
  },
)

const initialState = {
  foldersByClientId: {},
  foldersByParentAndClient: {},
  assetsByClientId: {},
  folderAssetsByClientId: {},
  statsByClientId: {},
  fetchFoldersStatus: "idle", // idle | loading | succeeded | failed
  fetchFoldersError: null,
  lastFetchedClientId: null,
  fetchAssetsStatus: "idle", // idle | loading | succeeded | failed
  fetchAssetsError: null,
  lastAssetsClientId: null,
  fetchFolderAssetsStatus: "idle", // idle | loading | succeeded | failed
  fetchFolderAssetsError: null,
  lastFetchedFolderKey: null,
  moveAssetsStatus: "idle", // idle | loading | succeeded | failed
  moveAssetsError: null,
  fetchStatsStatus: "idle", // idle | loading | succeeded | failed
  fetchStatsError: null,
  lastStatsClientId: null,
  createFolderStatus: "idle", // idle | loading | succeeded | failed
  createFolderError: null,
  updateFolderStatus: "idle", // idle | loading | succeeded | failed
  updateFolderError: null,
  deleteFolderStatus: "idle", // idle | loading | succeeded | failed
  deleteFolderError: null,
  downloadFolderStatus: "idle", // idle | loading | succeeded | failed
  downloadFolderError: null,
  downloadingFolderId: null,
  downloadAssetStatus: "idle", // idle | loading | succeeded | failed
  downloadAssetError: null,
  downloadingAssetId: null,
  uploadAssetsStatus: "idle", // idle | loading | succeeded | failed
  uploadAssetsError: null,
  deleteAssetsStatus: "idle", // idle | loading | succeeded | failed
  deleteAssetsError: null,
  updateAssetStatus: "idle", // idle | loading | succeeded | failed
  updateAssetError: null,
  updatingAssetId: null,
}

const mediaLibrarySlice = createSlice({
  name: "mediaLibrary",
  initialState,
  reducers: {
    resetCreateFolderState: (state) => {
      state.createFolderStatus = "idle"
      state.createFolderError = null
    },
    resetUpdateFolderState: (state) => {
      state.updateFolderStatus = "idle"
      state.updateFolderError = null
    },
    resetDeleteFolderState: (state) => {
      state.deleteFolderStatus = "idle"
      state.deleteFolderError = null
    },
    resetDownloadFolderState: (state) => {
      state.downloadFolderStatus = "idle"
      state.downloadFolderError = null
      state.downloadingFolderId = null
    },
    resetDownloadAssetState: (state) => {
      state.downloadAssetStatus = "idle"
      state.downloadAssetError = null
      state.downloadingAssetId = null
    },
    resetUploadAssetsState: (state) => {
      state.uploadAssetsStatus = "idle"
      state.uploadAssetsError = null
    },
    resetDeleteAssetsState: (state) => {
      state.deleteAssetsStatus = "idle"
      state.deleteAssetsError = null
    },
    resetUpdateAssetState: (state) => {
      state.updateAssetStatus = "idle"
      state.updateAssetError = null
      state.updatingAssetId = null
    },
    resetMoveAssetsState: (state) => {
      state.moveAssetsStatus = "idle"
      state.moveAssetsError = null
    },
    resetFetchFoldersState: (state) => {
      state.fetchFoldersStatus = "idle"
      state.fetchFoldersError = null
    },
    resetFetchAssetsState: (state) => {
      state.fetchAssetsStatus = "idle"
      state.fetchAssetsError = null
    },
    setFoldersForClient: (state, action) => {
      const { clientId, folders } = action.payload
      if (clientId) {
        state.foldersByClientId[clientId] = Array.isArray(folders) ? folders : []
      }
    },
    upsertMediaLibraryFolder: (state, action) => {
      const { clientId, folder } = action.payload
      if (!clientId || !folder?.id) return

      const normalized = normalizeApiFolder(folder)
      const id = String(normalized.id)
      const existing = state.foldersByClientId[clientId] ?? []
      const index = existing.findIndex((item) => String(item.id) === id)

      if (index >= 0) {
        existing[index] = {
          ...existing[index],
          ...normalized,
          sizeBytes: keepFolderSizeBytes(existing[index], normalized),
        }
      } else {
        state.foldersByClientId[clientId] = [...existing, normalized]
      }
    },
    clearMediaLibraryForClient: (state, action) => {
      const clientId = action.payload
      delete state.foldersByClientId[clientId]
      delete state.foldersByParentAndClient[clientId]
      delete state.assetsByClientId[clientId]
      delete state.folderAssetsByClientId[clientId]
      delete state.statsByClientId[clientId]
      if (String(state.lastFetchedClientId) === String(clientId)) {
        state.lastFetchedClientId = null
        state.fetchFoldersStatus = "idle"
        state.fetchFoldersError = null
      }
      if (String(state.lastAssetsClientId) === String(clientId)) {
        state.lastAssetsClientId = null
        state.fetchAssetsStatus = "idle"
        state.fetchAssetsError = null
      }
      if (String(state.lastFetchedFolderKey || "").startsWith(`${clientId}:`)) {
        state.lastFetchedFolderKey = null
        state.fetchFolderAssetsStatus = "idle"
        state.fetchFolderAssetsError = null
      }
      if (String(state.lastStatsClientId) === String(clientId)) {
        state.lastStatsClientId = null
        state.fetchStatsStatus = "idle"
        state.fetchStatsError = null
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMediaLibraryFolders.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.fetchFoldersStatus = "loading"
        }
        state.fetchFoldersError = null
      })
      .addCase(fetchMediaLibraryFolders.fulfilled, (state, action) => {
        const { clientId, folders, parentId, silent } = action.payload
        if (!silent) {
          state.fetchFoldersStatus = "succeeded"
          state.lastFetchedClientId = clientId
        }

        const normalizedFolders = normalizeFoldersForParentLevel(folders, parentId)
        const parentKey = folderParentKey(parentId)

        if (!state.foldersByParentAndClient[clientId]) {
          state.foldersByParentAndClient[clientId] = {}
        }
        const previousLevel = state.foldersByParentAndClient[clientId][parentKey] ?? []
        const previousById = new Map(previousLevel.map((folder) => [String(folder.id), folder]))
        state.foldersByParentAndClient[clientId][parentKey] = normalizedFolders.map((folder) => ({
          ...folder,
          sizeBytes: keepFolderSizeBytes(previousById.get(String(folder.id)), folder),
        }))

        const existing = state.foldersByClientId[clientId] ?? []
        state.foldersByClientId[clientId] = mergeFoldersForParentLevel(
          existing,
          normalizedFolders,
          parentId,
        )
      })
      .addCase(fetchMediaLibraryFolders.rejected, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.fetchFoldersStatus = "failed"
        }
        state.fetchFoldersError = action.payload
      })
      .addCase(fetchMediaLibraryAssets.pending, (state) => {
        state.fetchAssetsStatus = "loading"
        state.fetchAssetsError = null
      })
      .addCase(fetchMediaLibraryAssets.fulfilled, (state, action) => {
        const { clientId, assets } = action.payload
        state.fetchAssetsStatus = "succeeded"
        state.lastAssetsClientId = clientId
        state.assetsByClientId[clientId] = assets
      })
      .addCase(fetchMediaLibraryAssets.rejected, (state, action) => {
        state.fetchAssetsStatus = "failed"
        state.fetchAssetsError = action.payload
      })
      .addCase(fetchMediaLibraryFolderAssets.pending, (state, action) => {
        const { clientId, folderId, silent } = action.meta.arg
        if (!silent) {
          state.fetchFolderAssetsStatus = "loading"
          state.fetchFolderAssetsError = null
          state.lastFetchedFolderKey = `${clientId}:${String(folderId)}`
        }
      })
      .addCase(fetchMediaLibraryFolderAssets.fulfilled, (state, action) => {
        const { clientId, folderId, assets } = action.payload
        const { silent } = action.meta.arg
        if (!silent) {
          state.fetchFolderAssetsStatus = "succeeded"
          state.lastFetchedFolderKey = `${clientId}:${String(folderId)}`
        }
        if (!state.folderAssetsByClientId[clientId]) {
          state.folderAssetsByClientId[clientId] = {}
        }
        state.folderAssetsByClientId[clientId][folderId] = assets
      })
      .addCase(fetchMediaLibraryFolderAssets.rejected, (state, action) => {
        const { clientId, folderId, silent } = action.meta.arg
        if (!silent) {
          state.fetchFolderAssetsStatus = "failed"
          state.fetchFolderAssetsError = action.payload

          const folderKey = String(folderId)
          const folderAssetsMap = state.folderAssetsByClientId[clientId]
          if (folderAssetsMap && Object.prototype.hasOwnProperty.call(folderAssetsMap, folderKey)) {
            delete folderAssetsMap[folderKey]
          }
        }
      })
      .addCase(moveMediaLibraryAssets.pending, (state) => {
        state.moveAssetsStatus = "loading"
        state.moveAssetsError = null
      })
      .addCase(moveMediaLibraryAssets.fulfilled, (state, action) => {
        const { clientId, assetIds, targetFolderId, assets } = action.payload
        state.moveAssetsStatus = "succeeded"

        const movedIds = new Set(assetIds.map(String))
        const rootAssets = state.assetsByClientId[clientId] ?? []
        state.assetsByClientId[clientId] = rootAssets.filter((asset) => {
          const id = String(asset.id ?? asset.id_media ?? "")
          return !movedIds.has(id)
        })

        const folderAssetsMap = state.folderAssetsByClientId[clientId] ?? {}
        Object.keys(folderAssetsMap).forEach((folderId) => {
          folderAssetsMap[folderId] = (folderAssetsMap[folderId] ?? []).filter((asset) => {
            const id = String(asset.id ?? asset.id_media ?? "")
            return !movedIds.has(id)
          })
        })

        if (targetFolderId && assets?.length) {
          if (!state.folderAssetsByClientId[clientId]) {
            state.folderAssetsByClientId[clientId] = {}
          }
          const existing = state.folderAssetsByClientId[clientId][targetFolderId] ?? []
          const existingIds = new Set(existing.map((a) => String(a.id ?? a.id_media)))
          const merged = [
            ...existing,
            ...assets.filter((a) => !existingIds.has(String(a.id ?? a.id_media))),
          ]
          state.folderAssetsByClientId[clientId][targetFolderId] = merged
        }
      })
      .addCase(moveMediaLibraryAssets.rejected, (state, action) => {
        state.moveAssetsStatus = "failed"
        state.moveAssetsError = action.payload
      })
      .addCase(fetchMediaLibraryStats.pending, (state) => {
        state.fetchStatsStatus = "loading"
        state.fetchStatsError = null
      })
      .addCase(fetchMediaLibraryStats.fulfilled, (state, action) => {
        const { clientId, stats } = action.payload
        state.fetchStatsStatus = "succeeded"
        state.lastStatsClientId = clientId
        state.statsByClientId[clientId] = stats
      })
      .addCase(fetchMediaLibraryStats.rejected, (state, action) => {
        state.fetchStatsStatus = "failed"
        state.fetchStatsError = action.payload
      })
      .addCase(createMediaLibraryFolder.pending, (state) => {
        state.createFolderStatus = "loading"
        state.createFolderError = null
      })
      .addCase(createMediaLibraryFolder.fulfilled, (state, action) => {
        const { clientId, folder } = action.payload
        state.createFolderStatus = "succeeded"
        const parentKey = folderParentKey(folder.parentId)
        if (!state.foldersByParentAndClient[clientId]) {
          state.foldersByParentAndClient[clientId] = {}
        }
        const levelFolders = state.foldersByParentAndClient[clientId][parentKey] ?? []
        if (!levelFolders.some((item) => String(item.id) === String(folder.id))) {
          state.foldersByParentAndClient[clientId][parentKey] = [...levelFolders, folder]
        }
        const existing = state.foldersByClientId[clientId] ?? []
        const alreadyExists = existing.some((item) => String(item.id) === String(folder.id))
        state.foldersByClientId[clientId] = alreadyExists ? existing : [...existing, folder]
      })
      .addCase(createMediaLibraryFolder.rejected, (state, action) => {
        state.createFolderStatus = "failed"
        state.createFolderError = action.payload
      })
      .addCase(updateMediaLibraryFolder.pending, (state) => {
        state.updateFolderStatus = "loading"
        state.updateFolderError = null
      })
      .addCase(updateMediaLibraryFolder.fulfilled, (state, action) => {
        const { clientId, folder } = action.payload
        state.updateFolderStatus = "succeeded"
        const existing = state.foldersByClientId[clientId] ?? []
        state.foldersByClientId[clientId] = existing.map((item) =>
          item.id === folder.id ? { ...item, ...folder } : item,
        )
      })
      .addCase(updateMediaLibraryFolder.rejected, (state, action) => {
        state.updateFolderStatus = "failed"
        state.updateFolderError = action.payload
      })
      .addCase(deleteMediaLibraryFolder.pending, (state) => {
        state.deleteFolderStatus = "loading"
        state.deleteFolderError = null
      })
      .addCase(deleteMediaLibraryFolder.fulfilled, (state, action) => {
        const { clientId, descendantFolderIds } = action.payload
        state.deleteFolderStatus = "succeeded"
        const idsToDelete = new Set(descendantFolderIds.map(String))
        const existing = state.foldersByClientId[clientId] ?? []
        state.foldersByClientId[clientId] = existing.filter(
          (folder) => !idsToDelete.has(String(folder.id)),
        )

        const parentFoldersMap = state.foldersByParentAndClient[clientId]
        if (parentFoldersMap) {
          Object.keys(parentFoldersMap).forEach((parentKey) => {
            parentFoldersMap[parentKey] = (parentFoldersMap[parentKey] ?? []).filter(
              (folder) => !idsToDelete.has(String(folder.id)),
            )
          })
        }

        const folderAssetsMap = state.folderAssetsByClientId[clientId]
        if (folderAssetsMap) {
          idsToDelete.forEach((id) => {
            delete folderAssetsMap[id]
          })
        }
      })
      .addCase(deleteMediaLibraryFolder.rejected, (state, action) => {
        state.deleteFolderStatus = "failed"
        state.deleteFolderError = action.payload
      })
      .addCase(downloadMediaLibraryFolder.pending, (state, action) => {
        state.downloadFolderStatus = "loading"
        state.downloadFolderError = null
        state.downloadingFolderId = String(action.meta.arg.folderId)
      })
      .addCase(downloadMediaLibraryFolder.fulfilled, (state) => {
        state.downloadFolderStatus = "succeeded"
        state.downloadingFolderId = null
      })
      .addCase(downloadMediaLibraryFolder.rejected, (state, action) => {
        state.downloadFolderStatus = "failed"
        state.downloadFolderError = action.payload
        state.downloadingFolderId = null
      })
      .addCase(downloadMediaLibraryAsset.pending, (state, action) => {
        state.downloadAssetStatus = "loading"
        state.downloadAssetError = null
        state.downloadingAssetId = String(action.meta.arg.assetId)
      })
      .addCase(downloadMediaLibraryAsset.fulfilled, (state) => {
        state.downloadAssetStatus = "succeeded"
        state.downloadingAssetId = null
      })
      .addCase(downloadMediaLibraryAsset.rejected, (state, action) => {
        state.downloadAssetStatus = "failed"
        state.downloadAssetError = action.payload
        state.downloadingAssetId = null
      })
      .addCase(uploadMediaLibraryAssets.pending, (state) => {
        state.uploadAssetsStatus = "loading"
        state.uploadAssetsError = null
      })
      .addCase(uploadMediaLibraryAssets.fulfilled, (state) => {
        state.uploadAssetsStatus = "succeeded"
      })
      .addCase(uploadMediaLibraryAssets.rejected, (state, action) => {
        state.uploadAssetsStatus = "failed"
        state.uploadAssetsError = action.payload
      })
      .addCase(deleteMediaLibraryAssets.pending, (state) => {
        state.deleteAssetsStatus = "loading"
        state.deleteAssetsError = null
      })
      .addCase(deleteMediaLibraryAssets.fulfilled, (state, action) => {
        const { clientId, assetIds } = action.payload
        state.deleteAssetsStatus = "succeeded"

        const deletedIds = new Set(assetIds.map(String))
        const rootAssets = state.assetsByClientId[clientId] ?? []
        state.assetsByClientId[clientId] = rootAssets.filter((asset) => {
          const id = String(asset.id ?? asset.id_media ?? "")
          return !deletedIds.has(id)
        })

        const folderAssetsMap = state.folderAssetsByClientId[clientId] ?? {}
        Object.keys(folderAssetsMap).forEach((folderId) => {
          folderAssetsMap[folderId] = (folderAssetsMap[folderId] ?? []).filter((asset) => {
            const id = String(asset.id ?? asset.id_media ?? "")
            return !deletedIds.has(id)
          })
        })
      })
      .addCase(deleteMediaLibraryAssets.rejected, (state, action) => {
        state.deleteAssetsStatus = "failed"
        state.deleteAssetsError = action.payload
      })
      .addCase(updateMediaLibraryAsset.pending, (state, action) => {
        state.updateAssetStatus = "loading"
        state.updateAssetError = null
        state.updatingAssetId = String(action.meta.arg.assetId)
      })
      .addCase(updateMediaLibraryAsset.fulfilled, (state, action) => {
        const { clientId, assetId, name, asset } = action.payload
        state.updateAssetStatus = "succeeded"
        state.updatingAssetId = null

        const rootAssets = state.assetsByClientId[clientId] ?? []
        state.assetsByClientId[clientId] = applyAssetRenameToList(rootAssets, assetId, name, asset)

        const folderAssetsMap = state.folderAssetsByClientId[clientId] ?? {}
        Object.keys(folderAssetsMap).forEach((folderId) => {
          folderAssetsMap[folderId] = applyAssetRenameToList(
            folderAssetsMap[folderId],
            assetId,
            name,
            asset,
          )
        })
      })
      .addCase(updateMediaLibraryAsset.rejected, (state, action) => {
        state.updateAssetStatus = "failed"
        state.updateAssetError = action.payload
        state.updatingAssetId = null
      })
  },
})

export const {
  resetCreateFolderState,
  resetUpdateFolderState,
  resetDeleteFolderState,
  resetDownloadFolderState,
  resetDownloadAssetState,
  resetUploadAssetsState,
  resetDeleteAssetsState,
  resetUpdateAssetState,
  resetMoveAssetsState,
  resetFetchFoldersState,
  resetFetchAssetsState,
  setFoldersForClient,
  upsertMediaLibraryFolder,
  clearMediaLibraryForClient,
} = mediaLibrarySlice.actions

export default mediaLibrarySlice.reducer
